
import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { Capacitor } from '@capacitor/core';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Validates and processes an image URL to ensure it's usable
 * @param url - The image URL to validate
 * @param fallbackUrl - Optional fallback URL if the image can't be validated
 * @param isNewUpload - Whether this is a freshly uploaded image
 * @returns A valid image URL or the fallback
 */
export function validateImageUrl(
  url: string | undefined, 
  fallbackUrl: string = '/placeholder.svg',
  isNewUpload: boolean = false
): string {
  // If no URL provided, return fallback
  if (!url || url.trim() === '') {
    return fallbackUrl;
  }

  try {
    // Check if it's a relative path to a local asset (like our logo files)
    if (url.startsWith('/')) {
      return url;
    }

    // Check if it's a blob URL (which is valid but might be temporary)
    if (url.startsWith('blob:')) {
      return url;
    }

    // Check if it's a data URI
    if (url.startsWith('data:')) {
      return url;
    }

    // Handle Supabase storage URLs - completely strip query parameters
    // This is the key fix for most of our image loading issues
    if (url.includes('supabase.co/storage') || url.includes('object/public') || url.includes('guest/')) {
      // For Supabase URLs, always use the base URL without any parameters
      // This prevents conflicts with various caching mechanisms
      return url.split('?')[0];
    }

    // Check if the URL is already a valid URL format
    if (url.startsWith('http://') || url.startsWith('https://')) {
      try {
        new URL(url);
        return url;
      } catch (e) {
        console.error("Invalid URL format:", url, e);
        return fallbackUrl;
      }
    }

    // If we get to this point, the URL format is not recognized
    console.warn("Unrecognized image URL format:", url);
    return fallbackUrl;
  } catch (error) {
    // If URL construction fails, the URL is invalid
    console.error("Invalid URL format:", url, error);
    return fallbackUrl;
  }
}

// This is a mock storage implementation that should NOT be used for real uploads
// Use the uploadImageToStorage function from src/integrations/supabase/storage.ts instead
export const storage = {
  ref: (path: string) => ({
    path
  }),
  uploadBytes: async (ref: any, file: File) => {
    try {
      console.warn("WARNING: Using mock uploadBytes instead of Supabase storage!");
      console.log("Mock uploadBytes called with path:", ref.path, "file:", file.name);
      
      // If the file is a blob or base64, convert it to a proper URL
      let fileUrl = '';
      if (file instanceof Blob) {
        fileUrl = URL.createObjectURL(file);
      }
      
      return {
        ref,
        metadata: {
          name: file.name,
          contentType: file.type,
          size: file.size,
          url: fileUrl
        }
      };
    } catch (error) {
      console.error("Error in mock uploadBytes:", error);
      throw error;
    }
  },
  getDownloadURL: async (ref: any) => {
    try {
      console.warn("WARNING: Using mock getDownloadURL instead of Supabase storage!");
      console.log("getDownloadURL called with ref:", ref);
      
      // If the ref path is a blob URL, return it directly
      if (ref.path.startsWith('blob:')) {
        console.log("Returning blob URL directly:", ref.path);
        return ref.path;
      }
      
      // If the path is already a full URL (from Supabase or other storage), return it directly
      if (ref.path.startsWith('http')) {
        console.log("Using existing URL:", ref.path);
        return ref.path;
      }
      
      // For testing without real storage, return the placeholder
      console.log("Using placeholder for path:", ref.path);
      return "https://placeholder.pics/svg/300x240?text=Image";
    } catch (error) {
      console.error("Error in mock getDownloadURL:", error);
      return "https://placeholder.pics/svg/300x240?text=Error";
    }
  }
};

/**
 * Checks if the app is running in a Capacitor (native mobile) environment.
 */
export const isCapacitorApp = (): boolean => {
  return Capacitor.isNativePlatform();
};

